import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, Gift, Check, MessageCircle } from "lucide-react";
import { useSeo } from "@/lib/seo";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

const entryKey = (e) => `${e.id}::${e.size || ""}::${e.colour || ""}`;

export default function SharedWishlist() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: false, owner: "", items: [] });
  const [name, setName] = useState(() => localStorage.getItem("hb_gifter_name") || "");
  useSeo("A shared wishlist", "Someone's Happy Bulldog Co. wishlist — perfect for gifting.");

  useEffect(() => {
    api.get(`/wishlist/shared/${token}`)
      .then(({ data }) => setState({ loading: false, error: false, owner: data.owner_name || "", items: data.items || [] }))
      .catch(() => setState({ loading: false, error: true, owner: "", items: [] }));
  }, [token]);

  const toggleClaim = async (e) => {
    try {
      const gifter = name.trim();
      if (gifter) localStorage.setItem("hb_gifter_name", gifter);
      const { data } = await api.post(`/wishlist/shared/${token}/claim`,
        { product_id: e.id, size: e.size, colour: e.colour, name: gifter });
      setState((s) => ({
        ...s,
        items: s.items.map((it) => entryKey(it) === entryKey(e) ? { ...it, claimed: data.claimed, claimed_by: data.claimed_by } : it),
      }));
      toast.success(data.claimed ? "Marked as claimed — thanks! 🎁" : "Released — it's up for grabs again.");
    } catch {
      toast.error("Couldn't update that. Please try again.");
    }
  };

  if (state.loading) {
    return <div className="max-w-7xl mx-auto px-4 py-24"><div className="h-64 bg-white/60 rounded-3xl animate-pulse" /></div>;
  }

  if (state.error) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="shared-wishlist-error">
        <Heart className="w-16 h-16 text-[#FF6B9D]/40 mx-auto" />
        <h1 className="font-display font-extrabold text-2xl text-[#19324A] mt-4">Wishlist not found</h1>
        <p className="text-[#19324A]/60 mt-2">This share link may have expired or is incorrect.</p>
        <Link to="/shop" className="btn-primary mt-6 inline-flex">Browse the Shop</Link>
      </div>
    );
  }

  const title = state.owner ? `${state.owner}'s Wishlist` : "A Shared Wishlist";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 min-h-[60vh]" data-testid="shared-wishlist">
      <div className="bg-[#FFD93D]/40 rounded-3xl px-6 py-5 mb-6 flex items-start gap-3">
        <Gift className="w-7 h-7 text-[#19324A] shrink-0" />
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#19324A]">{title} 🐾</h1>
          <p className="text-[#19324A]/70 text-sm">Here are the goodies they're hoping for — add any to your bag as a gift, and mark it claimed so no one double-buys.</p>
        </div>
      </div>

      {state.items.length > 0 && (
        <div className="mb-8 flex items-center gap-2 max-w-sm">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40}
            data-testid="gifter-name-input" placeholder="Your name (optional, for claims)"
            className="flex-1 rounded-full border border-[#19324A]/15 bg-white px-4 py-2.5 text-sm text-[#19324A] placeholder:text-[#19324A]/40 focus:outline-none focus:ring-2 focus:ring-[#FFD93D]" />
        </div>
      )}

      {state.items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl soft-shadow">
          <Heart className="w-16 h-16 text-[#FF6B9D]/40 mx-auto" />
          <p className="font-display font-bold text-xl text-[#19324A] mt-4">This wishlist is empty right now.</p>
          <Link to="/shop" className="btn-primary mt-6 inline-flex">Browse the Shop</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="shared-wishlist-grid">
          {state.items.map((e) => {
            const variant = [e.colour, e.size].filter(Boolean).join(" · ");
            return (
              <div key={entryKey(e)} className={`flex flex-col gap-2 transition-opacity ${e.claimed ? "opacity-70" : ""}`}
                data-testid={`shared-item-${e.product.slug}`}>
                <div className="relative">
                  <ProductCard product={e.product} />
                  {e.claimed && (
                    <span className="absolute top-3 left-3 z-20 px-2.5 py-1 rounded-full text-[11px] font-display font-bold bg-[#7ED957] text-[#19324A] shadow-sm inline-flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" /> Claimed
                    </span>
                  )}
                </div>
                {variant && (
                  <span className="self-start text-[11px] px-2.5 py-1 rounded-full bg-[#4CC9F0]/25 text-[#19324A] font-display font-bold">
                    Wants: {variant}
                  </span>
                )}
                {e.note && (
                  <p className="text-xs text-[#19324A]/75 flex items-start gap-1" data-testid={`shared-note-${e.product.slug}`}>
                    <MessageCircle className="w-3.5 h-3.5 text-[#FF6B9D] shrink-0 mt-0.5" /> {e.note}
                  </p>
                )}
                <button onClick={() => toggleClaim(e)} data-testid={`shared-claim-${e.product.slug}`}
                  className={`w-full !py-2 text-sm rounded-full font-display font-bold transition-colors ${e.claimed
                    ? "bg-white border-2 border-[#7ED957] text-[#19324A] hover:bg-[#7ED957]/10"
                    : "bg-[#7ED957] text-[#19324A] hover:brightness-95"}`}>
                  {e.claimed ? `Claimed${e.claimed_by ? ` by ${e.claimed_by}` : ""} — release?` : "I'll get this 🎁"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
